import { useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi, IDatasource, IGetRowsParams } from 'ag-grid-community';
import he from 'he';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import { fetchConversationsPage, CONVERSATIONS_PAGE_SIZE } from '../../hooks/useConversationMessages';
import type { ChatConversation } from '../../types/resource';

const CONV_ROW_HEIGHT = 152; // fixed (matches Angular row-height="152")

function participantsLabel(c: ChatConversation): string {
  const names = (c.participants ?? []).map((p) => p.name || p.id).filter(Boolean);
  return names.length ? names.join(', ') : 'Unknown';
}

// Cell renderer for a conversation list row — port of conversationCellRenderer: avatar,
// participants, a 3-line preview of the most-recent message (text or attachment), timestamp.
function ConversationRow(params: { data?: ChatConversation; activeConversationId?: string | null }) {
  const c = params.data;
  if (!c) return <span aria-hidden="true" />; // placeholder row (block still loading)
  const m = c.mostRecentMessage;
  const text = m?.text ? he.decode(m.text).trim() : '';
  const firstAttachment = m?.attachments?.[0];
  // Active class lives on .iio-conv-row itself (same element as :hover) so the highlight
  // and hover-gray never fight across elements — no leftover blue sliver.
  const isActive = c.conversationId === params.activeConversationId;

  return (
    <div className={`iio-conv-row${isActive ? ' active' : ''}`}>
      <div className="iio-conv-avatar">
        <i className="fas fa-user-circle" />
      </div>
      <div className="iio-conv-main">
        <h5 className="iio-conv-participants">{participantsLabel(c)}</h5>
        {text ? (
          <div className="iio-conv-preview">{text}</div>
        ) : firstAttachment ? (
          firstAttachment.imageType && !firstAttachment.infected ? (
            <img className="iio-conv-thumb" src={firstAttachment.path} alt="" />
          ) : (
            <div className="iio-conv-file">
              <i className="far fa-file fa-2x" /> <small>{firstAttachment.fileName}</small>
            </div>
          )
        ) : null}
      </div>
      <div className="iio-conv-time">{m?.formattedDateTimeSent}</div>
    </div>
  );
}

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
  activeConversationId?: string | null;
  focusIndex?: number | null;
  onSelect: (conversation: ChatConversation) => void;
}

export default function ConversationList({
  projectId,
  collectionId,
  resourceId,
  activeConversationId,
  focusIndex,
  onSelect,
}: Props) {
  const apiRef = useRef<GridApi | null>(null);
  const focusDoneRef = useRef(false);
  // When opened without an anchor conversation (phone-image .xml has no chatMessageId),
  // auto-select the first conversation once so the messages column isn't empty (matches
  // Angular's DataGridFirstPageRenderedEvent). Captured at mount so it doesn't retrigger.
  const autoSelectRef = useRef(!activeConversationId);

  // Redraw so the row cells re-render with the new activeConversationId (passed via
  // cellRendererParams below) and the .active highlight follows selection changes.
  useEffect(() => {
    apiRef.current?.redrawRows();
  }, [activeConversationId]);

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const page = Math.floor(params.startRow / CONVERSATIONS_PAGE_SIZE);
        const res = await fetchConversationsPage(projectId, collectionId, resourceId, page);
        params.successCallback(res.conversations, res.total);
        if (page === 0 && autoSelectRef.current && res.conversations[0]) {
          autoSelectRef.current = false;
          onSelect(res.conversations[0]);
        }
        if (!focusDoneRef.current && focusIndex != null) {
          focusDoneRef.current = true;
          apiRef.current?.ensureIndexVisible(focusIndex, 'middle');
        }
      } catch {
        params.failCallback();
      }
    },
  };

  useEffect(() => {
    focusDoneRef.current = false;
  }, [focusIndex]);

  return (
    <div className="ag-theme-bootstrap iio-conv-list" style={{ height: '100%', width: '100%' }}>
      <AgGridReact
        rowModelType="infinite"
        datasource={datasource}
        cacheBlockSize={CONVERSATIONS_PAGE_SIZE}
        infiniteInitialRowCount={CONVERSATIONS_PAGE_SIZE}
        rowHeight={CONV_ROW_HEIGHT}
        rowSelection="single"
        suppressContextMenu
        suppressCellSelection
        headerHeight={0}
        columnDefs={[
          {
            colId: 'conversation',
            flex: 1,
            sortable: false,
            resizable: false,
            cellRendererFramework: ConversationRow,
            cellRendererParams: { activeConversationId },
            cellStyle: { whiteSpace: 'normal', lineHeight: 'normal', cursor: 'pointer' },
          },
        ]}
        onGridReady={({ api }) => {
          apiRef.current = api;
        }}
        onRowClicked={(e) => {
          if (e.data) onSelect(e.data as ChatConversation);
        }}
      />
    </div>
  );
}
